import { useMutation } from "@apollo/client";
import { Collapse } from "@mui/material";
import { RHFormConsumerTextField } from "@RHCommerceDev/component-rh-text-field/RHFormConsumerTextField";
import { EmailField } from "@RHCommerceDev/component-standard-fields";
import { usePageContentNoProxy } from "@RHCommerceDev/custom-providers/LocationProvider";
import {
  emailAtom,
  useSetAuthenticationDialogCurrentScreenAtom,
  useSetCurrentCartId,
  useSetIsAuthenticationDialogAtom,
  useUserSessionSetAtom
} from "@RHCommerceDev/hooks/atoms";
import useAsync from "@RHCommerceDev/hooks/useAsync";
import { useEnv } from "@RHCommerceDev/hooks/useEnv";
import { processEnvServer } from "@RHCommerceDev/hooks/useSsrHooks";
import { Button } from "@RHCommerceDev/unstyled-ui-components/Button";
import { Typography } from "@RHCommerceDev/unstyled-ui-components/Typography";
import { useStytch } from "@stytch/react";
import analyticsLoader from "analytics/loader";
import { stytchSuccessMutation } from "graphql-client/queries/user";
import { isCreateAccount } from "@RHCommerceDev/hooks-atoms";

import { useLocalization } from "@RHCommerceDev/hooks/useLocalization";
import { useResetPasswordTemplate } from "@RHCommerceDev/hooks/useResetPasswordTemplate";
import { checkCountryIsEu } from "@RHCommerceDev/utils/checkCountryIsEu";
import { LOGIN_EXPIRATION_MINUTES } from "@RHCommerceDev/utils/constants";
import getCountryFromUrl, {
  getCountryLanguageFromURL
} from "@RHCommerceDev/utils/getCountryFromUrl";
import memoize from "@RHCommerceDev/utils/memoize";
import { PasswordEyeIcon } from "icons/PasswordEyeIcon";
import { ShowPassword } from "icons/ShowPasswordIcon";
import { useAtomValue, useSetAtom } from "jotai";
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import useGetCartProjectLazyQuery from "@RHCommerceDev/hooks/queries/useGetCartProjectLazyQuery";
import Cookies from "universal-cookie";
import RHSpinner from "components/RHSpinner";
import { useCreateAccountTemplate } from "@RHCommerceDev/hooks/useCreateAccountTemplate";
import { useIsCartPages } from "@RHCommerceDev/hooks/useIsCartPages";
import { getClientOrigin } from "@RHCommerceDev/utils/getClientOrigin";
import { emailRegex } from "utils/regex";

const SigninWithEmailAndPassword = () => {
  const setAuthenticationDialogCurrentScreen =
    useSetAuthenticationDialogCurrentScreenAtom();
  const setUserSessionAtom = useUserSessionSetAtom();
  const setCurrentCartId = useSetCurrentCartId();
  const { isShoppingCartPage } = useIsCartPages();
  const [isEmailValid, setIsEmailValid] = React.useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const country = getCountryFromUrl();
  const setCreateAtom = useSetAtom(isCreateAccount);
  const language = getCountryLanguageFromURL();
  const prefix = useLocalization();
  const countryEU = checkCountryIsEu({ country });
  const region = countryEU ? "UK" : "US";
  const { pageContent } = usePageContentNoProxy();
  const stytchClient = useStytch();
  const emailId = useAtomValue(emailAtom);
  const setEmailValue = useSetAtom(emailAtom);
  const stytchData = pageContent?.emailSignupContent;
  const setOpenAuthenticationDialog = useSetIsAuthenticationDialogAtom();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const templateId = useResetPasswordTemplate();
  const templateIdAccount = useCreateAccountTemplate();
  const [getCartProjection] = useGetCartProjectLazyQuery();

  const env = useEnv();
  const form = useForm({
    defaultValues: {
      email: "",
      password: ""
    }
  });
  const watch = form.watch(); // watch password field to apply styles
  const hostName = processEnvServer
    ? getClientOrigin()
    : window?.location?.origin;
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = e.target.value;
    if (emailValue.length > 0) {
      setErrorMessage("");
    }
    setIsEmailValid(!!emailValue.match(emailRegex));
  };

  const handlePasswordOnBlur = e => {
    if (form.getValues("password")?.length === 0) {
      setErrorMessage(stytchData?.PASSWORD_IS_REQUIRED);
    }
  };

  const handleEmailOnBlur = e => {
    if (form.getValues("email")?.length === 0) {
      setErrorMessage(stytchData?.Email_address_is);
    }
  };

  const [isScreenTransitioning, setIsScreenTransitioning] = useState(false);

  const handleSignInSuccess = () => {
    setIsLoading(false);
    setIsScreenTransitioning(true);
    setOpenAuthenticationDialog(false);
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(prev => !prev);
  };

  const [stytchSuccess] = useMutation<Mutation>(stytchSuccessMutation, {
    context: {
      fetchOptions: {
        method: "POST"
      }
    },
    onCompleted: ({ stytchSuccess }) => {
      setUserSessionAtom(prevData => ({
        ...prevData,
        ...stytchSuccess,
        ...(isShoppingCartPage && { isCartMerge: true })
      }));
      setCurrentCartId(stytchSuccess?.currentCartId as string);
      getCartProjection({ id: stytchSuccess?.rhuid });
    },
    onError() {}
  });

  useEffect(() => {
    if (emailId) {
      form.setValue("email", emailId);
      setIsEmailValid(true);
    }
  }, [emailId]);

  const { run, loading } = useAsync(async values => {
    setIsLoading(true);
    try {
      const stytchUser = await stytchClient.passwords.authenticate({
        ...values,
        session_duration_minutes: 129600
      });

      if (stytchUser?.user?.emails?.[0]?.verified === false) {
        await stytchClient.magicLinks.email.send(
          stytchUser?.user?.emails?.[0]?.email,
          {
            login_magic_link_url: location.pathname.includes(
              "checkout/confirmation"
            )
              ? `${hostName}?redirect_url=${prefix}?type=signup`
              : `${hostName}?redirect_url=${location.pathname}?type=signup`,
            login_expiration_minutes: 1440,
            login_template_id: templateIdAccount
          }
        );
        setCreateAtom(true);
        setOpenAuthenticationDialog(true);
        return setAuthenticationDialogCurrentScreen("CheckYourInbox");
      }

      if (!processEnvServer && stytchUser?.user?.trusted_metadata?.rh_user_id) {
        analyticsLoader(a =>
          a.emitAnalyticsEvent(
            document.querySelector("#spa-root > *")! as HTMLElement,
            a.EVENTS.LOGIN.INT_TYPE,
            {
              userId: stytchUser?.user?.trusted_metadata?.rh_user_id,
              loggedIn: "Success"
            }
          )
        );
      }

      if (stytchUser?.user?.untrusted_metadata?.is2FA === true) {
        setIsLoading(false);
        setAuthenticationDialogCurrentScreen("SmsCodeHandler");
      } else {
        await stytchSuccess();
        handleSignInSuccess();
      }
      setErrorMessage("");
    } catch (error: any) {
      if (!processEnvServer) {
        analyticsLoader(a =>
          a.emitAnalyticsEvent(
            document.querySelector("#spa-root > *")! as HTMLElement,
            a.EVENTS.LOGIN.INT_TYPE,
            {
              loggedIn: "Failure"
            }
          )
        );
      }

      // Check for "reset password" error type
      if (error.error_type === "reset_password") {
        setEmailValue(values.email);
        try {
          await stytchClient.passwords.resetByEmailStart({
            email: values.email,
            reset_password_redirect_url: hostName,
            reset_password_expiration_minutes: LOGIN_EXPIRATION_MINUTES,
            reset_password_template_id: templateId,
            login_redirect_url: hostName
          });
          setIsLoading(false);
          setAuthenticationDialogCurrentScreen("CheckYourInbox"); // Navigate to Check Your Inbox screen
        } catch (error) {
          console.error(error);
        }
        return;
      }

      // If the error type is "unauthorized_credentials", set the custom error message
      if (error.error_type === "unauthorized_credentials") {
        setIsLoading(false);
        setErrorMessage(stytchData?.Password_is_required);
        //  ("The email address or password is incorrect. Please try again.");
        return;
      }

      if (stytchData?.Stytch_Error_Object.hasOwnProperty(error.error_type)) {
        setIsLoading(false);
        setErrorMessage(stytchData?.Stytch_Error_Object[error.error_type]);
        return;
      }

      const errorMessage =
        error.error_message || "An unexpected error occurred.";
      setIsLoading(false);
      setErrorMessage(stytchData?.Password_is_required);
    }
  });

  const onSubmit = React.useCallback(
    async values => {
      /** PE-7010 make sure the old session is actually cleared */
      const cookies = new Cookies();
      const sessionId = cookies.get("stytch_session");
      if (sessionId) {
        await stytchClient.session.revoke();
      }
      setTimeout(() => {
        run(values);
      }, 500);
    },
    [run, stytchClient.session]
  );

  const handleResetPasswordByEmail = React.useCallback(() => {
    setAuthenticationDialogCurrentScreen("ResetPasswordByEmail");
  }, [setAuthenticationDialogCurrentScreen]);

  const handleSignUpWithEmailAndPassword = React.useCallback(() => {
    setAuthenticationDialogCurrentScreen("SignUpWithEmailAndPassword");
  }, [setAuthenticationDialogCurrentScreen]);

  const handleSignInWithASecureLink = React.useCallback(() => {
    setAuthenticationDialogCurrentScreen("SignInWithaSecureLink");
  }, [setAuthenticationDialogCurrentScreen]);

  const redirect_url = useMemo(() => {
    return `${env.REACT_APP_KEYCLOAK_ORIGIN}${
      env.REACT_APP_KEYCLOAK_PATH
    }/realms/${
      env.REACT_APP_KEYCLOAK_REALM
    }/protocol/openid-connect/auth?client_id=${
      env.REACT_APP_KEYCLOAK_TRADE_CLIENTID
    }&redirect_uri=${hostName}${prefix}/trade-sales/trade-sign-in.jsp&state=12e20d80-73e0-41d1-9fbe-96742e4e073c&response_mode=fragment&response_type=code&scope=openid&nonce=e244c199-7a2b-445c-856e-a2671a4b2578&region=${region}&country=${country}&language=${language}&ui_locales=${
      language.split("-")[0]
    }`;
  }, [
    country,
    hostName,
    env.REACT_APP_KEYCLOAK_ORIGIN,
    env.REACT_APP_KEYCLOAK_PATH,
    env.REACT_APP_KEYCLOAK_REALM,
    env.REACT_APP_KEYCLOAK_TRADE_CLIENTID,
    language,
    prefix,
    region
  ]);

  return (
    <div
      className={`flex h-auto w-[311px] flex-col items-center transition-all duration-300 ${
        isScreenTransitioning ? "opacity-0" : "opacity-100"
      }`}
    >
      <Typography
        variant="h1"
        className="!font-75 font-primary-rh-thin leading-[23px]"
      >
        {stytchData?.SIGN_IN}
      </Typography>
      <div className="mt-4 flex h-auto w-[314px] flex-wrap items-center justify-center gap-1 whitespace-nowrap sm:h-4 sm:flex-nowrap">
        <Typography
          variant={"p"}
          className="font-primary-rhlight !font-light leading-[16.5px]"
        >
          {stytchData?.Please_enter_your}
        </Typography>
        <Button
          variant="link"
          className="!m-0 !p-0 decoration-[0.5px] !underline-offset-[3px]"
          onClick={handleSignUpWithEmailAndPassword}
        >
          <Typography
            variant="caption"
            className="cursor-pointer font-primary-rhlight !text-[11px] !font-light leading-[13.2px] tracking-[0.44px] "
          >
            {stytchData?.CREATE_AN_ACCOUNT}
          </Typography>
        </Button>
      </div>
      <div
        className={`relative w-full ${
          isLoading ? "pointer-events-none opacity-50" : ""
        }`}
      >
        <FormProvider {...form}>
          <form
            className="!mt-4 flex w-[311px] flex-col"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="relative flex h-[57px] w-full items-end">
              <EmailField
                onChange={handleEmailChange}
                onBlur={handleEmailOnBlur}
                variant="outlined"
                label={stytchData?.Email}
                value={watch?.email}
                className={`font-primary-rh flex h-[48px] justify-center [&>p]:text-xxsm [&>p]:!font-light [&>p]:leading-[12.1px] [&>p]:tracking-[0.44px] [&>p]:!text-rh-red [&>p]:font-rhsans${
                  watch?.email?.length
                    ? "!border-[0.5px] [&>div>fieldset]:!border-black"
                    : ""
                } `}
                disabled={isLoading}
              />
            </div>
            <div className="flex w-full flex-col ">
              <div>
                <Collapse
                  in={isEmailValid}
                  className={`${
                    !isEmailValid ? "!mt-0 max-h-0" : "!mt-2 max-h-14 "
                  }`}
                >
                  <div className="font-primary-rh relative  w-full">
                    <RHFormConsumerTextField
                      type={passwordVisible ? "text" : "password"}
                      autoComplete="off"
                      name="password"
                      fullWidth
                      inputProps={{ maxLength: 20 }}
                      label={stytchData?.Password}
                      className={`flex justify-center [&>p]:mb-[8px] [&>p]:!font-rhsans [&>p]:!text-xxsm [&>p]:!font-light [&>p]:leading-[12.1px] [&>p]:!text-rh-red ${
                        watch?.password?.length
                          ? "!border-[0.5px] [&>div>fieldset]:!border-black"
                          : ""
                      } `}
                      onChange={() => setErrorMessage("")}
                      onBlur={handlePasswordOnBlur}
                      disabled={isLoading}
                    />

                    <span
                      className="absolute right-3 top-6 -translate-y-1/2 transform cursor-pointer p-0"
                      onClick={togglePasswordVisibility}
                      role="button"
                      onKeyDown={e => {
                        // Ensure the button itself is focused before handling the keydown event
                        if (
                          e.currentTarget === document.activeElement &&
                          (e.key === "Enter" || e.key === " ")
                        ) {
                          e.preventDefault(); // Prevent default behavior
                          togglePasswordVisibility();
                        }
                      }}
                      tabIndex={0} // Ensure the button is focusable
                    >
                      {passwordVisible ? <ShowPassword /> : <PasswordEyeIcon />}
                    </span>
                  </div>
                </Collapse>
                {errorMessage && (
                  <Typography
                    className={`mt-1 h-auto font-rhsans text-xxsm font-light leading-[12.1px] text-rh-red ${
                      errorMessage ? "visible" : "invisible"
                    }`}
                  >
                    {errorMessage}
                  </Typography>
                )}
              </div>
              <div
                className={`my-2  flex h-[29px] items-center justify-end font-primary-rhlight font-light`}
              >
                <Button
                  variant="link"
                  className="h-[16px] p-0 font-primary-rhlight !text-[11px] !font-light leading-[16.5px] no-underline"
                  size="none"
                  type="button"
                  id="forgot-password"
                  onClick={handleResetPasswordByEmail}
                >
                  {stytchData?.Forgot_password}
                </Button>
              </div>
            </div>
            <Button
              type="submit"
              variant="secondary"
              disabled={loading || !isEmailValid}
              id="sign-in"
              className={`h-[48px] w-[311px] ${
                loading || !isEmailValid ? "cursor-not-allowed bg-[#DDD]" : ""
              }`}
            >
              {isLoading ? <RHSpinner /> : stytchData?.SIGN_IN}
            </Button>
            <div className="mt-4 flex h-[14px] content-between items-center justify-between">
              <div className="flex">
                <Button
                  id="sign-in-with-a-secure-link"
                  variant="link"
                  className="flex h-[13px] w-[123px] text-wrap !p-0 !font-light decoration-[0.5px] !underline-offset-[3px] sm:inline-flex sm:text-nowrap"
                >
                  <Typography
                    variant="caption"
                    className="w-auto text-left font-primary-rhlight !text-[11px] leading-[13.2px] sm:text-nowrap"
                    onClick={handleSignInWithASecureLink}
                  >
                    {stytchData?.SIGN_IN_WITH}
                  </Typography>
                </Button>
              </div>
              <div className="flex w-[113px]">
                <a
                  href={redirect_url}
                  className="font-primary-rhlight text-[11px] !font-light leading-[13.2px] underline decoration-[0.5px] underline-offset-[3px]"
                >
                  {stytchData?.Trade_Program_Sign}
                </a>
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default memoize(SigninWithEmailAndPassword);
